/**
 * Pure, testable builders for Plan Version Editor mutations (no axios/network dependency).
 * Guarantees: prices are integer USD cents; only explicitly-set allowances are sent; -1 means
 * Unlimited; a reason is always required (never an unaudited mutation).
 */
export const PLAN_LIMIT_KEYS = ['max_staff', 'monthly_engagements', 'active_integrations', 'monthly_ai_resolutions', 'storage_gb', 'active_vehicles', 'active_trackers'];

/** PATCH body for saving a draft version from the editor form. */
export function buildDraftPatch(form, reason, limitKeys = PLAN_LIMIT_KEYS) {
  const trimmed = (reason || '').trim();
  if (!trimmed) throw new Error('REASON_REQUIRED');
  const payload = { reason: trimmed };
  // GOV13-A4 — editable plan-version display name (identity key `version` stays immutable; this is the
  // human-facing name only). Sent only when provided so unrelated saves never blank it.
  if (form.display_name != null && String(form.display_name).trim() !== '') {
    payload.display_name = String(form.display_name).trim();
  }
  if (form.price_cents !== '' && form.price_cents != null) {
    const c = Number(form.price_cents);
    if (!Number.isInteger(c) || c < 0) throw new Error('PRICE_CENTS_INVALID');
    payload.price_cents = c;
  }
  if (form.annual_price_cents !== '' && form.annual_price_cents != null) {
    const a = Number(form.annual_price_cents);
    if (!Number.isInteger(a) || a < 0) throw new Error('ANNUAL_PRICE_CENTS_INVALID');
    payload.annual_price_cents = a;
  }
  const limits = {};
  for (const k of limitKeys) {
    const v = form.limits ? form.limits[k] : undefined;
    if (v !== '' && v != null) {
      const n = Number(v);
      if (!Number.isInteger(n) || n < -1) throw new Error('ALLOWANCE_INVALID');
      limits[k] = n;   // -1 = Unlimited
    }
  }
  payload.limits = limits;

  // D18-C: ISO currency + SEPARATE wallet credit allocations (never one ambiguous combined field).
  if (form.currency != null && String(form.currency).trim() !== '') {
    payload.currency = String(form.currency).trim().toUpperCase();
  }
  const ai = form.included_ai_operations;
  const comm = form.included_communication;
  const aiSet = ai !== '' && ai != null;
  const commSet = comm !== '' && comm != null;
  if (aiSet || commSet) {
    const ic = {};
    if (aiSet) { const n = Number(ai); if (!Number.isInteger(n) || n < 0) throw new Error('AI_OPS_CREDITS_INVALID'); ic.ai_operations = n; }
    if (commSet) { const n = Number(comm); if (!Number.isInteger(n) || n < 0) throw new Error('COMMUNICATION_CREDITS_INVALID'); ic.communication = n; }
    payload.included_credits = ic;
  }

  // GOV13-A4 — per-plan FEATURE composition: explicit { feature_key: boolean } map (mirrors the seed's model;
  // the entitlements resolver treats absent == false). Only sent when the editor supplies a composition map,
  // so unrelated saves (price/limits only) stay backward-compatible and never wipe existing features.
  if (form.features && typeof form.features === 'object' && !Array.isArray(form.features)) {
    const feats = {};
    for (const [k, on] of Object.entries(form.features)) {
      if (k) feats[k] = !!on;   // preserve explicit false (excluded), never coerce to absence
    }
    payload.features = feats;
  }
  // GOV13-A4 — INTEGRATION composition: de-duplicated array of canonical connector keys (never free text).
  if (Array.isArray(form.connectors)) {
    payload.connectors = Array.from(new Set(form.connectors.filter((c) => typeof c === 'string' && c)));
  }
  return payload;
}

/** Body for clone-to-draft. */
export function buildClonePayload(label, reason) {
  const l = (label || '').trim();
  const r = (reason || '').trim();
  if (!l) throw new Error('LABEL_REQUIRED');
  if (!r) throw new Error('REASON_REQUIRED');
  return { version: l, reason: r };
}

/** Body for publish (immutable). */
export function buildPublishPayload(reason) {
  const r = (reason || '').trim();
  if (!r) throw new Error('REASON_REQUIRED');
  return { reason: r };
}
